<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.resources.editTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div v-if="loading && !form.title" class="modal-body loading-state">{{ $t('bo.common.loading') }}</div>

      <template v-else>
        <div class="modal-body">
          <div v-if="error" class="error-banner">{{ error }}</div>

          <div class="form-group">
            <label>{{ $t('bo.resources.labelTitle') }} <span class="required">*</span></label>
            <input v-model="form.title" type="text" class="input" maxlength="150" />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ $t('bo.resources.colType') }} <span class="required">*</span></label>
              <select v-model="form.type" class="input">
                <option v-for="tp in TYPES" :key="tp.value" :value="tp.value">{{ tp.label }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ $t('bo.resources.colAudience') }}</label>
              <select v-model="form.target_audience" class="input">
                <option value="">{{ $t('bo.resources.audienceBoth') }}</option>
                <option value="mentors">{{ $t('bo.resources.audienceMentors') }}</option>
                <option value="mentees">{{ $t('bo.resources.audienceMentees') }}</option>
                <option value="both">{{ $t('bo.resources.audienceBoth') }}</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('bo.resources.labelAvailability') }}</label>
            <select v-model="form.availability" class="input">
              <option value="generic">{{ $t('bo.resources.availabilityGeneric') }}</option>
              <option value="program_specific">{{ $t('bo.resources.availabilityProgramSpecific') }}</option>
            </select>
            <p class="field-hint">{{ $t('bo.resources.availabilityHint') }}</p>
          </div>

          <div class="form-group">
            <label>{{ $t('bo.resources.labelLink') }} <span class="required">*</span></label>
            <input v-model="form.link" type="url" class="input" maxlength="2000" />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea v-model="form.description" class="textarea" rows="3" maxlength="2000" />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.resources.labelCoverImage') }}</label>
            <CoverImageInput v-model="form.cover_image_url" />
          </div>

          <!-- Localisation : générer les autres langues via le moteur, puis éditer -->
          <div v-if="otherLocales.length" class="localization">
            <div class="loc-head">
              <h3 class="loc-title">{{ $t('bo.resources.localizationTitle') }}</h3>
              <button type="button" class="btn-generate" :disabled="localizing" @click="generate">
                {{ localizing ? $t('bo.resources.generating') : $t('bo.resources.generateTranslations') }}
              </button>
            </div>
            <p class="loc-hint">{{ $t('bo.resources.localizationHint') }}</p>

            <div class="loc-tabs">
              <button
                v-for="l in otherLocales"
                :key="l.code"
                type="button"
                class="loc-tab"
                :class="{ active: activeLocale === l.code }"
                @click="activeLocale = l.code"
              >
                {{ l.label || l.code }}
                <span v-if="isAuto(l.code)" class="auto-badge">{{ $t('bo.resources.autoBadge') }}</span>
              </button>
            </div>

            <template v-if="activeLocale && translations[activeLocale]">
              <div class="form-group">
                <label>{{ $t('bo.resources.labelTitle') }}</label>
                <input v-model="translations[activeLocale].title" type="text" class="input" maxlength="150" />
              </div>
              <div class="form-group">
                <label>{{ $t('bo.common.description') }}</label>
                <textarea v-model="translations[activeLocale].description" class="textarea" rows="3" maxlength="2000" />
              </div>
              <div class="form-group">
                <label>{{ $t('bo.resources.labelLink') }}</label>
                <input v-model="translations[activeLocale].link" type="url" class="input" maxlength="2000" />
                <p class="field-hint">{{ $t('bo.resources.localizationLinkHint') }}</p>
              </div>
            </template>
          </div>

          <div v-if="resource" class="lifecycle-actions">
            <div class="lifecycle-label">{{ $t('bo.resources.lifecycleActions') }}</div>
            <div class="lifecycle-btns">
              <button
                v-if="resource.status === 'draft'"
                @click="handlePublish"
                :disabled="actionLoading === 'publish'"
                class="btn-lifecycle btn-publish"
              >{{ actionLoading === 'publish' ? $t('bo.resources.publishing') : $t('bo.resources.publish') }}</button>
              <button
                v-if="resource.status !== 'archived'"
                @click="handleArchive"
                :disabled="actionLoading === 'archive'"
                class="btn-lifecycle btn-archive"
              >{{ actionLoading === 'archive' ? $t('bo.resources.archiving') : $t('bo.resources.archive') }}</button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button @click="submit" :disabled="loading || !canSubmit" class="btn-submit">
            {{ loading ? $t('bo.common.saving') : $t('bo.common.save') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import adminResourceService from '@/services/adminResourceService'
import translationService from '@/services/translationService'
import CoverImageInput from '@/components/ui/CoverImageInput.vue'

const { t } = useI18n()

const props = defineProps({ resource: { type: Object, required: true } })
const emit = defineEmits(['close', 'updated'])

// --- Localisation (langues autres que la langue source) ---
const locales = ref([])
const defaultLocale = ref('en')
const translations = ref({}) // { [code]: { title, description, link, is_auto_generated } }
const original = ref({})
const activeLocale = ref('')
const localizing = ref(false)

const otherLocales = computed(() => locales.value.filter((l) => l.code !== defaultLocale.value))

const isAuto = (code) => !!translations.value[code]?.is_auto_generated

const loadLocales = async () => {
  try {
    const data = await translationService.getLocales()
    locales.value = Array.isArray(data) ? data : []
    defaultLocale.value = locales.value.find((l) => l.is_default)?.code || 'en'
  } catch {
    locales.value = []
  }
}

const loadTranslations = async () => {
  try {
    const res = await adminResourceService.getTranslations(props.resource.id)
    const rows = res.data?.data || []
    const map = {}
    rows.forEach((r) => {
      map[r.locale] = {
        title: r.title || '',
        description: r.description || '',
        link: r.link || '',
        is_auto_generated: !!r.is_auto_generated,
      }
    })
    translations.value = map
    original.value = JSON.parse(JSON.stringify(map))
  } catch {
    // Section localisation non bloquante.
  }
}

const generate = async () => {
  localizing.value = true
  error.value = ''
  try {
    await adminResourceService.localize(props.resource.id)
    await loadTranslations()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.resources.failedLocalize')
  } finally {
    localizing.value = false
  }
}

onMounted(async () => {
  await loadLocales()
  await loadTranslations()
  if (!activeLocale.value) activeLocale.value = otherLocales.value[0]?.code || ''
})

const TYPES = computed(() => [
  { value: 'article', label: t('bo.resources.types.article') },
  { value: 'video', label: t('bo.resources.types.video') },
  { value: 'document', label: t('bo.resources.types.document') },
  { value: 'template', label: t('bo.resources.types.template') },
  { value: 'tool', label: t('bo.resources.types.tool') },
  { value: 'slides', label: t('bo.resources.types.slides') },
  { value: 'link', label: t('bo.resources.types.link') },
  { value: 'course', label: t('bo.resources.types.course') },
  { value: 'other', label: t('bo.resources.types.other') },
])

const loading = ref(false)
const actionLoading = ref(null)
const error = ref('')
const form = ref({
  title: props.resource.title || '',
  type: props.resource.type || '',
  link: props.resource.link || '',
  description: props.resource.description || '',
  cover_image_url: props.resource.cover_image_url || '',
  target_audience: props.resource.target_audience || '',
  availability: props.resource.availability || 'generic',
})

const canSubmit = computed(() => form.value.title.trim() && form.value.type && form.value.link.trim())

const submit = async () => {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  try {
    const payload = { ...form.value }
    if (!payload.description) payload.description = null
    if (!payload.cover_image_url) payload.cover_image_url = null
    if (!payload.target_audience) payload.target_audience = null
    await adminResourceService.update(props.resource.id, payload)

    // Persiste les traductions éditées (langues autres que la source).
    for (const l of otherLocales.value) {
      const cur = translations.value[l.code]
      const ori = original.value[l.code]
      const changed =
        cur &&
        (!ori ||
          cur.title !== ori.title ||
          cur.description !== ori.description ||
          cur.link !== ori.link)
      if (changed && (cur.title?.trim() || cur.description?.trim() || cur.link?.trim())) {
        await adminResourceService.saveTranslation(props.resource.id, l.code, {
          title: cur.title?.trim() || null,
          description: cur.description?.trim() || null,
          link: cur.link?.trim() || null,
        })
      }
    }

    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.resources.failedUpdate')
  } finally {
    loading.value = false
  }
}

const handlePublish = async () => {
  if (!confirm(t('bo.resources.confirmPublish'))) return
  actionLoading.value = 'publish'
  error.value = ''
  try {
    await adminResourceService.publish(props.resource.id)
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.resources.failedPublish')
    actionLoading.value = null
  }
}

const handleArchive = async () => {
  if (!confirm(t('bo.resources.confirmArchive'))) return
  actionLoading.value = 'archive'
  error.value = ''
  try {
    await adminResourceService.archive(props.resource.id)
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.resources.failedArchive')
    actionLoading.value = null
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.loading-state {
  color: #6b7280;
  text-align: center;
  padding: 3rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.required { color: #dc2626; }

.form-row {
  display: flex;
  gap: 1rem;
}

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
  background: white;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.localization {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: #f9fafb;
}

.loc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.loc-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.loc-hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

.field-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0;
}

.btn-generate {
  padding: 0.4rem 0.9rem;
  border: 1px solid #3b82f6;
  background: white;
  color: #2563eb;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loc-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.loc-tab {
  padding: 0.3rem 0.7rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.loc-tab.active {
  border-color: #3b82f6;
  color: #2563eb;
  background: #eff6ff;
}

.auto-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
  background: #fef3c7;
  padding: 0.05rem 0.35rem;
  border-radius: 9999px;
}

.lifecycle-actions {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
}

.lifecycle-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 0.75rem;
}

.lifecycle-btns {
  display: flex;
  gap: 0.75rem;
}

.btn-lifecycle {
  padding: 0.4rem 1rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}

.btn-publish { background: #d1fae5; color: #065f46; }
.btn-publish:hover:not(:disabled) { background: #a7f3d0; }
.btn-archive { background: #f3f4f6; color: #6b7280; }
.btn-archive:hover:not(:disabled) { background: #e5e7eb; }
.btn-lifecycle:disabled { opacity: 0.5; cursor: not-allowed; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
